import { mdiAlertCircleOutline, mdiCheck } from "@mdi/js"
import type {
  AppointmentPlanningPreviewRow as ApiAppointmentPlanningPreviewRow,
  AppointmentPlanningManualAppointmentPayload,
  AppointmentPlanningManualAppointmentType,
  AppointmentPlanningPreviewAppointment,
  AppointmentPlanningPreviewWarning,
  AppointmentPlanningStrategy,
  PlanningUnitContactContext,
} from "../../../../../../api/kehrblatt/planning"
import {
  formatPropertyAddressLinesWithUnit,
  formatPropertyUnitLabel,
} from "../../../../../../_shared/_logic/unit-labels"
import { internalOrderTypeLabel } from "../../../../../../_shared/_logic/order-type-labels"
import { buildPropertyObjectTagPresentation } from "../../../_logic/object-tags"
import {
  appendPlanningContactSubLabel,
  planningContactLabel,
  planningContactsTooltipList,
  planningUnitContactContextKey,
  planningUnitContactContextMap,
} from "../../../_logic/planning-contact-contexts"
import {
  buildRichTooltip,
  tooltipText,
  tooltipTitle,
} from "../../../../../../_shared/_logic/rich-tooltips"
import { buildWorkOrderSummaryTagPresentation } from "../../../_logic/summary-tag"
import type {
  AppointmentPlanningExistingSourceRow,
  AppointmentPlanningWorkOrderOverride,
  ManualFlexSlotOptionsState,
} from "../../../_logic/appointments-contract"
import {
  projectAppointmentDuration,
  reconcileAppointmentDeadlineWarnings,
} from "./appointmentProjection"
import { formatDateOnly } from "../../../_logic/dates"
import type {
  PlanningPreviewRow,
  PlanningPreviewSelectOption,
} from "../_shared/_logic/table-contract"
import { appointmentContactLabel, buildAppointmentTag } from "./appointmentTags"
import {
  buildEmployeeShortLabelPresentations,
  decorateEmployeeOptionsWithShortLabels,
} from "./employeeShortLabels"
import { buildRouteAnchorTag } from "./routeAnchorTags"

export type BuildExistingWorkOrderPlanningPreviewRowsInput = {
  rows: AppointmentPlanningExistingSourceRow[]
  previewByRowId: Map<string, ApiAppointmentPlanningPreviewRow>
  eventByWorkOrderId?: Map<string, ScheduleEvent>
  committedRowIds?: ReadonlySet<string>
  workOrderOverridesByRowId: Record<string, AppointmentPlanningWorkOrderOverride>
  employeeOptions: PlanningPreviewSelectOption[]
  employeeLabelById: Record<string, string>
  manualFlexSlotOptionsByRowId?: Record<string, ManualFlexSlotOptionsState>
  unitContactContexts?: PlanningUnitContactContext[]
}

const ensureOption = (
  options: PlanningPreviewSelectOption[],
  value: string,
  labelById: Record<string, string>,
): PlanningPreviewSelectOption[] => {
  if (!value || options.some((option) => option.value === value)) return options

  return [...options, { value, label: labelById[value] ?? "Mitarbeiter" }]
}

const ensureOptions = (
  options: PlanningPreviewSelectOption[],
  values: string[],
  labelById: Record<string, string>,
): PlanningPreviewSelectOption[] =>
  values.reduce((current, value) => ensureOption(current, value, labelById), options)

const labelForEmployees = (
  employeeIds: string[],
  employeeLabelById: Record<string, string>,
  fallback: string,
) => {
  const labels = employeeIds.map((id) => employeeLabelById[id] ?? "Mitarbeiter").filter(Boolean)

  return labels.length ? labels.join(", ") : fallback
}

const normalizeSearchText = (...values: Array<unknown>): string =>
  values
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase("de-DE")

const resolveAppointmentEmployeeLabel = (
  employeeId: string,
  appointment: Pick<AppointmentPlanningPreviewAppointment, "employee_name"> | null | undefined,
  employeeLabelById: Record<string, string>,
): string => appointment?.employee_name || employeeLabelById[String(employeeId)] || "Mitarbeiter"

const appointmentSortValue = (
  appointment: Pick<AppointmentPlanningPreviewAppointment, "start_at"> | null | undefined,
) => {
  if (!appointment?.start_at) return null

  const timestamp = Date.parse(appointment.start_at)

  return Number.isFinite(timestamp) ? timestamp : null
}

const planningStrategyOptions = (
  preview?: ApiAppointmentPlanningPreviewRow | null,
): PlanningPreviewSelectOption[] => {
  const values = preview?.available_planning_strategies?.length
    ? preview.available_planning_strategies
    : (["AUTO", "MANUAL"] as AppointmentPlanningStrategy[])
  const labelByValue: Record<AppointmentPlanningStrategy, string> = {
    AUTO: "Automatisch",
    MANUAL: "Manuell",
    KEEP_EXISTING: "Bestehenden übernehmen",
  }

  return values.map((value) => ({ value, label: labelByValue[value] ?? value }))
}

const appointmentTypeForAppointment = (
  appointment?: { flex_type?: string | null } | null,
): AppointmentPlanningManualAppointmentType => (appointment?.flex_type === "SOFT" ? "FLEX" : "HARD")

const appointmentFromScheduleEvent = (
  event: ScheduleEvent | null | undefined,
  employeeLabelById: Record<string, string>,
): AppointmentPlanningPreviewAppointment | null => {
  if (!event?.start_at || !event.end_at || !event.employee_id) return null

  const employeeId = String(event.employee_id)

  return {
    start_at: event.start_at,
    end_at: event.end_at,
    employee_id: employeeId,
    employee_name: event.employee_name ?? employeeLabelById[employeeId] ?? null,
    flex_type: event.flex_type ?? "HARD",
    timeframe_start_at: event.timeframe_start_at ?? null,
    timeframe_end_at: event.timeframe_end_at ?? null,
    previous_anchor: null,
    next_anchor: null,
  }
}

const idleManualFlexSlotOptionsState = (): ManualFlexSlotOptionsState => ({ status: "idle" })

const manualFlexDateForRow = (
  override?: AppointmentPlanningWorkOrderOverride | null,
  appointment?: AppointmentPlanningPreviewAppointment | null,
): string => {
  const explicit = formatDateOnly(override?.manual_flex_date)

  if (explicit) return explicit

  return formatDateOnly(
    override?.manual_flex_appointment?.timeframe_start_at ??
      override?.manual_flex_appointment?.start_at ??
      override?.manual_appointment?.timeframe_start_at ??
      override?.manual_appointment?.start_at ??
      appointment?.timeframe_start_at ??
      appointment?.start_at ??
      null,
  )
}

const previewAppointmentFromManual = (
  manual: AppointmentPlanningManualAppointmentPayload | null | undefined,
  employeeLabelById: Record<string, string>,
): AppointmentPlanningPreviewAppointment | null => {
  if (!manual?.start_at || !manual.end_at || !manual.employee_id) return null

  return {
    start_at: manual.start_at,
    end_at: manual.end_at,
    employee_id: manual.employee_id,
    employee_name: employeeLabelById[String(manual.employee_id)] ?? null,
    flex_type: manual.flex_type,
    timeframe_start_at: manual.timeframe_start_at ?? null,
    timeframe_end_at: manual.timeframe_end_at ?? null,
    previous_anchor: null,
    next_anchor: null,
  }
}

const effectivePlanningStrategy = ({
  override,
  preview,
  activeAppointment,
  defaultStrategy,
}: {
  override?: AppointmentPlanningWorkOrderOverride | null
  preview?: ApiAppointmentPlanningPreviewRow | null
  activeAppointment?: AppointmentPlanningPreviewAppointment | null
  defaultStrategy?: AppointmentPlanningStrategy | null
}): AppointmentPlanningStrategy => {
  const explicit =
    override?.planning_strategy ?? preview?.effective_planning_strategy ?? defaultStrategy

  if (explicit === "AUTO" || explicit === "MANUAL" || explicit === "KEEP_EXISTING") return explicit

  return activeAppointment ? "KEEP_EXISTING" : "AUTO"
}

const appointmentForStrategy = ({
  strategy,
  previewAppointment,
  manualAppointment,
  activeAppointment,
  employeeLabelById,
}: {
  strategy: AppointmentPlanningStrategy
  previewAppointment?: AppointmentPlanningPreviewAppointment | null
  manualAppointment?: AppointmentPlanningManualAppointmentPayload | null
  activeAppointment?: AppointmentPlanningPreviewAppointment | null
  employeeLabelById: Record<string, string>
}): AppointmentPlanningPreviewAppointment | null => {
  if (strategy === "KEEP_EXISTING") return activeAppointment ?? previewAppointment ?? null

  if (strategy === "MANUAL")
    return previewAppointmentFromManual(manualAppointment, employeeLabelById)

  return previewAppointment ?? null
}

const appointmentEmployeeOptionsForRow = ({
  employeeOptions,
  allowedEmployeeIds,
  currentEmployeeId,
  employeeLabelById,
}: {
  employeeOptions: PlanningPreviewSelectOption[]
  allowedEmployeeIds: string[]
  currentEmployeeId: string
  employeeLabelById: Record<string, string>
}): PlanningPreviewSelectOption[] => {
  const allowed = new Set(allowedEmployeeIds.map(String).filter(Boolean))
  const scoped = employeeOptions.filter((option) => {
    const value = String(option.value)

    return !allowed.size || allowed.has(value)
  })

  return !allowed.size || allowed.has(currentEmployeeId)
    ? ensureOption(scoped, currentEmployeeId, employeeLabelById)
    : scoped
}

const appointmentEmployeeIdFromAppointment = (
  appointment?: Pick<AppointmentPlanningPreviewAppointment, "employee_id"> | null,
): string => String(appointment?.employee_id ?? "").trim()

const effectiveAppointmentEmployeeId = ({
  override,
  appointment,
}: {
  override?: AppointmentPlanningWorkOrderOverride | null
  appointment?: Pick<AppointmentPlanningPreviewAppointment, "employee_id"> | null
}): string => {
  const overrideEmployeeId = String(override?.appointment_employee_id ?? "").trim()

  return overrideEmployeeId || appointmentEmployeeIdFromAppointment(appointment)
}

const restrictAppointmentEmployeeToAllowed = (
  employeeId: string,
  allowedEmployeeIds: string[],
): string => {
  const normalizedEmployeeId = String(employeeId ?? "").trim()

  if (!normalizedEmployeeId) return ""

  const allowed = new Set(allowedEmployeeIds.map(String).filter(Boolean))

  return !allowed.size || allowed.has(normalizedEmployeeId) ? normalizedEmployeeId : ""
}

const buildAddressLines = (
  property: Property,
  unitId: string | null,
  fallbackLabel?: string | null,
) => {
  const lines = formatPropertyAddressLinesWithUnit(property, unitId, unitId ? fallbackLabel : null)
  const propertyRecord = property as Property & { address?: string | null; street?: string | null }

  return lines.length
    ? lines
    : [fallbackLabel || propertyRecord.address || propertyRecord.street || "Liegenschaft"]
}

const buildPropertyTooltip = (
  property: Property,
  unitId: string | null,
  fallbackLabel?: string | null,
  contactContext?: PlanningUnitContactContext | null,
) =>
  buildRichTooltip([
    tooltipTitle("Liegenschaft"),
    ...buildAddressLines(property, unitId, fallbackLabel).map((line) => tooltipText(line)),
    planningContactsTooltipList(contactContext),
  ])

const warningShortLabel = (warning?: AppointmentPlanningPreviewWarning | null) => {
  switch (warning?.code) {
    case "DUPLICATE_OPEN_WORK_ORDER":
      return "Offener Auftrag"
    case "SHORT_NOTICE_ANNOUNCEMENT_WINDOW":
      return "Kurzfristig"
    case "ROTATING_EMPLOYEE_NOT_ALLOWED":
      return "Mitarbeiter prüfen"
    case "NO_ALLOWED_EMPLOYEE":
      return "Kein Mitarbeiter"
    case "NO_SLOT_FOUND":
      return "Kein Termin"
    case "INBOX_PREVIEW_ROW_ERROR":
      return "Datenfehler"
    case "AFTER_DUE_DATE":
      return "Nach Frist"
    case "ROUTE_FAR_FROM_DAY_CLUSTER":
      return "Tagescluster"
    case "BATCH_PLANNING_EARLY_DAY_UNDERFILLED":
      return "Dünner Tag"
    case "CONTACT_AVAILABILITY_FALLBACK_OUTSIDE_WINDOW":
      return "Kontaktzeit"
    case "CONTACT_AVAILABILITY_CONTACT_OUT_OF_SCOPE":
      return "Ansprechpartner"
    default:
      return "Prüfbedarf"
  }
}

const visibleWarnings = (warnings: AppointmentPlanningPreviewWarning[]) =>
  warnings.filter((warning) => warning.code !== "DUPLICATE_OPEN_WORK_ORDER")

const buildWarningTag = (warnings: AppointmentPlanningPreviewWarning[]) => {
  const entries = visibleWarnings(warnings)

  if (!entries.length) {
    return {
      label: "Nein",
      color: "neutral",
      icon: mdiCheck,
      tooltip: "Keine Warnung für diese Zeile.",
    }
  }

  const hasError = entries.some((warning) => warning.severity === "error")

  return {
    label: "Ja",
    subLabel: warningShortLabel(entries[0]),
    color: hasError ? "rose" : "amber",
    icon: mdiAlertCircleOutline,
    tooltip: buildRichTooltip([
      tooltipTitle(hasError ? "Nicht planbar" : "Warnungen"),
      ...entries.map((warning) => tooltipText(warning.message)),
    ]),
    tooltipHtml: true,
  }
}

const workOrderUnitId = (order: WorkOrder): string | null =>
  order.property_unit_id ? String(order.property_unit_id) : null

const workOrderUnitFallbackLabel = (order: WorkOrder): string | null => {
  const label = String(order.property_unit_label ?? "").trim()

  if (label) return formatPropertyUnitLabel(label)

  return workOrderUnitId(order) ? "Einheit nicht geladen" : null
}

const workOrderPropertyTag = (
  order: WorkOrder,
  property: Property | null,
  contactContext?: PlanningUnitContactContext | null,
) => {
  const propertyUnitId = workOrderUnitId(order)
  const fallbackUnitLabel = workOrderUnitFallbackLabel(order)

  if (!property) {
    return {
      label: fallbackUnitLabel || "Liegenschaft",
      subLabel: "nicht geladen",
      color: "#64748b",
      icon: null,
      tooltip: "Die Liegenschaft konnte für diesen Auftrag nicht geladen werden.",
    }
  }

  const propertyTag = buildPropertyObjectTagPresentation({
    property,
    propertyUnitId,
    fallbackUnitLabel,
    mode: "address-first",
  })

  return {
    ...propertyTag,
    subLabel: appendPlanningContactSubLabel(propertyTag.subLabel, contactContext),
    tooltip: buildPropertyTooltip(property, propertyUnitId, fallbackUnitLabel, contactContext),
  }
}

export const buildExistingWorkOrderPlanningPreviewRows = ({
  rows,
  previewByRowId,
  eventByWorkOrderId,
  committedRowIds = new Set(),
  workOrderOverridesByRowId,
  employeeOptions,
  employeeLabelById,
  manualFlexSlotOptionsByRowId = {},
  unitContactContexts = [],
}: BuildExistingWorkOrderPlanningPreviewRowsInput): PlanningPreviewRow[] => {
  const employeeShortLabels = buildEmployeeShortLabelPresentations(employeeOptions)
  const contactContextByKey = planningUnitContactContextMap(unitContactContexts)

  return rows.map((row) => {
    const override = workOrderOverridesByRowId[row.id] ?? {}
    const preview = previewByRowId.get(row.id) ?? previewByRowId.get(row.workOrder.id)
    const activeAppointment: AppointmentPlanningPreviewAppointment | null =
      preview?.active_schedule_event ??
      appointmentFromScheduleEvent(eventByWorkOrderId?.get(row.workOrder.id), employeeLabelById)
    const strategy = effectivePlanningStrategy({
      override,
      preview,
      activeAppointment,
      defaultStrategy: row.defaultPlanningStrategy,
    })
    const manualAppointment = override.manual_appointment ?? null
    const appointment = appointmentForStrategy({
      strategy,
      previewAppointment: preview?.appointment ?? null,
      manualAppointment,
      activeAppointment,
      employeeLabelById,
    })
    const warnings = reconcileAppointmentDeadlineWarnings({
      warnings: preview?.warnings ?? [],
      appointment,
      dueDate: row.workOrder.due_date,
    })
    const allowedEmployeeIds = (row.workOrder.allowed_employee_ids ?? [])
      .map(String)
      .filter(Boolean)
    const appointmentEmployeeId = restrictAppointmentEmployeeToAllowed(
      effectiveAppointmentEmployeeId({
        override,
        appointment,
      }),
      allowedEmployeeIds,
    )
    const propertyId = String(row.workOrder.property_id ?? row.property?.id ?? "")
    const propertyUnitId = workOrderUnitId(row.workOrder)
    const contactContext =
      contactContextByKey.get(planningUnitContactContextKey(propertyId, propertyUnitId)) ?? null
    const propertyTag = workOrderPropertyTag(row.workOrder, row.property, contactContext)
    const appointmentEmployeeLabel = resolveAppointmentEmployeeLabel(
      appointmentEmployeeId,
      appointment,
      employeeLabelById,
    )

    return {
      id: row.id,
      committed: committedRowIds.has(row.id),
      propertyId,
      unitId: propertyUnitId,
      propertyTag,
      workOrderSummaryTag: buildWorkOrderSummaryTagPresentation({ workOrder: row.workOrder }),
      orderTypeValue: internalOrderTypeLabel(row.workOrder) ?? "Auftrag",
      estimatedDurationValue: row.workOrder.estimated_duration_min
        ? String(row.workOrder.estimated_duration_min)
        : "",
      dueDateValue: formatDateOnly(row.workOrder.due_date),
      allowedEmployeeIds,
      allowedEmployeeLabel: labelForEmployees(
        allowedEmployeeIds,
        employeeLabelById,
        "Alle ausgewählten",
      ),
      allowedEmployeeOptions: decorateEmployeeOptionsWithShortLabels(
        ensureOptions(employeeOptions, allowedEmployeeIds, employeeLabelById),
        employeeShortLabels,
      ),
      appointmentEmployeeId,
      appointmentEmployeeLabel,
      appointmentEmployeeOptions: decorateEmployeeOptionsWithShortLabels(
        appointmentEmployeeOptionsForRow({
          employeeOptions,
          allowedEmployeeIds,
          currentEmployeeId: appointmentEmployeeId,
          employeeLabelById,
        }),
        employeeShortLabels,
      ),
      appointmentEmployeeEditable: true,
      planningStrategy: strategy,
      planningStrategyOptions: planningStrategyOptions(preview),
      appointmentTypeValue:
        override.manual_appointment_type ??
        appointmentTypeForAppointment(
          manualAppointment ?? appointment ?? activeAppointment ?? null,
        ),
      appointmentTypeEditable: strategy === "MANUAL",
      manualAppointment,
      manualFlexDate: manualFlexDateForRow(override, appointment),
      manualFlexSlotId: override.manual_flex_slot_id ?? "",
      manualFlexSlotOptionsState:
        manualFlexSlotOptionsByRowId[row.id] ?? idleManualFlexSlotOptionsState(),
      appointmentStartAt: appointment?.start_at ?? null,
      appointmentEndAt: appointment?.end_at ?? null,
      appointmentTimeframeStartAt: appointment?.timeframe_start_at ?? null,
      appointmentTimeframeEndAt: appointment?.timeframe_end_at ?? null,
      appointmentSortValue: appointmentSortValue(appointment),
      resolvedAppointmentContact: preview?.resolved_appointment_contact ?? null,
      customerAvailabilityMatch: appointment?.customer_availability_match ?? null,
      appointmentTag: buildAppointmentTag(
        appointment,
        employeeLabelById,
        preview?.resolved_appointment_contact ?? null,
        appointment?.customer_availability_match ?? null,
        { keep: strategy === "KEEP_EXISTING", event: eventByWorkOrderId?.get(row.workOrder.id) },
      ),
      arrivalAnchor: buildRouteAnchorTag(appointment?.previous_anchor, "arrival"),
      departureAnchor: buildRouteAnchorTag(appointment?.next_anchor, "departure"),
      warningTag: buildWarningTag(warnings),
      searchText: normalizeSearchText(
        propertyTag.label,
        propertyTag.subLabel,
        propertyTag.tooltip,
        row.workOrder.property_unit_label,
        appointmentContactLabel(preview?.resolved_appointment_contact ?? null),
        ...(contactContext?.contacts ?? []).map(planningContactLabel),
        internalOrderTypeLabel(row.workOrder) ?? "Auftrag",
      ),
      removable: false,
      dirty: Boolean(
        override.appointment_employee_id ||
        override.appointment_contact_customer_id ||
        override.planning_strategy ||
        override.manual_appointment ||
        override.manual_flex_date ||
        override.manual_flex_slot_id ||
        override.manual_flex_appointment,
      ),
    }
  })
}
